import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { commandCenterApi } from '../services/api';
import { AlertTriangle, X, ExternalLink, CheckCircle2 } from 'lucide-react';

const DISMISS_KEY = 'cc_broken_resumes_dismissed_at';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const CLEANUP_KEY = 'cc_cleanup_just_ran';
const CLEANUP_FRESH_MS = 5 * 60 * 1000; // show "fixed N" for up to 5 min after cleanup
const CLEANUP_AUTO_HIDE_MS = 30 * 1000; // auto-fade the green banner after 30s on screen

/**
 * Small banner that appears on Command Center.
 *
 *  - GREEN "Cleaned up N candidate(s)" confirmation immediately after the admin
 *    runs the cleanup tool from /settings (handed off via sessionStorage).
 *    Auto-hides after 30s.
 *  - AMBER warning otherwise, when the pipeline contains broken resume links.
 *    Dismissible for 24h per browser.
 *
 * Cheap — single GET on Command Center mount.
 */
const BrokenResumesBanner = ({ user }) => {
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const at = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      return at && Date.now() - at < DISMISS_TTL_MS;
    } catch {
      return false;
    }
  });

  // Pick up the cleanup-just-ran handoff from sessionStorage (set by Settings)
  const [cleanupResult, setCleanupResult] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CLEANUP_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.at || Date.now() - parsed.at > CLEANUP_FRESH_MS) {
        sessionStorage.removeItem(CLEANUP_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  // Fetch the live count
  useEffect(() => {
    let alive = true;
    commandCenterApi
      .getBrokenResumesCount()
      .then((r) => {
        if (!alive) return;
        setCount(r.data?.count || 0);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Auto-hide green confirmation after CLEANUP_AUTO_HIDE_MS
  useEffect(() => {
    if (!cleanupResult) return;
    const remaining = Math.max(
      0,
      CLEANUP_AUTO_HIDE_MS - (Date.now() - cleanupResult.at)
    );
    const t = setTimeout(() => {
      sessionStorage.removeItem(CLEANUP_KEY);
      setCleanupResult(null);
    }, remaining);
    return () => clearTimeout(t);
  }, [cleanupResult]);

  // 1) Green confirmation takes precedence
  if (cleanupResult) {
    return (
      <div
        className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 flex items-center gap-3"
        data-testid="cc-cleanup-success-banner"
      >
        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900 leading-tight">
            Cleaned up {cleanupResult.fixed} candidate
            {cleanupResult.fixed === 1 ? '' : 's'}
          </p>
          <p className="text-xs text-emerald-800/80 leading-snug mt-0.5">
            Stale resume references cleared. Affected candidates can now re-upload
            their resumes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            sessionStorage.removeItem(CLEANUP_KEY);
            setCleanupResult(null);
          }}
          aria-label="Dismiss"
          className="text-emerald-700 hover:text-emerald-900 flex-shrink-0"
          data-testid="cc-cleanup-success-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // 2) Otherwise show the amber warning when the pipeline still has broken refs
  if (!loaded || count === 0 || dismissed) return null;

  const isAdmin = user?.role === 'admin' || user?.role === 'delivery_manager';

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3"
      data-testid="cc-broken-resumes-banner"
    >
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="w-4 h-4 text-amber-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900 leading-tight">
          {count} candidate{count === 1 ? '' : 's'} in your pipeline{' '}
          {count === 1 ? 'has' : 'have'} a broken resume link
        </p>
        <p className="text-xs text-amber-800/80 leading-snug mt-0.5">
          {isAdmin
            ? 'Run the cleanup tool to clear stale references — candidates can then re-upload.'
            : 'Ask your admin to run the cleanup tool from Settings.'}
        </p>
      </div>
      {isAdmin && (
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition flex-shrink-0"
          data-testid="cc-broken-resumes-fix"
        >
          Fix now
          <ExternalLink className="w-3 h-3" />
        </Link>
      )}
      <button
        type="button"
        onClick={() => {
          try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
          } catch {
            /* ignore */
          }
          setDismissed(true);
        }}
        aria-label="Dismiss"
        className="text-amber-700 hover:text-amber-900 flex-shrink-0"
        data-testid="cc-broken-resumes-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default BrokenResumesBanner;
